import { create } from 'zustand'
import { Achievement } from '../types'
import { apiClient } from '../utils/api'
import getSubscription from '../utils/subscriptions'

interface AchievementStore {
  achievements: Achievement[]
  loading: boolean
  error: string | null
  fetchAchievements: (playerId: string) => Promise<void>
  addAchievement: (achievement: Omit<Achievement, 'id' | 'created_at' | 'updated_at'>) => Promise<void>
  deleteAchievement: (id: string) => Promise<void>
}

const ACHIEVEMENT_LIMIT_FREE = 3

export const useAchievementStore = create<AchievementStore>()(
  (set) => ({
    achievements: [],
    loading: false,
    error: null,

    fetchAchievements: async (playerId: string) => {
      set({ loading: true, error: null })
      try {
        const achievements = await apiClient.getAchievements(playerId)
        set({ achievements, loading: false })
      } catch (error) {
        set({ error: 'Failed to load achievements', loading: false })
        console.error('Failed to fetch achievements:', error)
      }
    },

    addAchievement: async (achievement) => {
      const sub = await getSubscription()
      const isPro = !!(sub && (sub.status === 'active' || sub.active))
      if (!isPro) {
        const state = useAchievementStore.getState()
        if (state.achievements.length >= ACHIEVEMENT_LIMIT_FREE) {
          const err = new Error('FREE_LIMIT')
          throw err
        }
      }
      set({ error: null })
      try {
        const newAchievement = await apiClient.createAchievement(achievement)
        set((state) => ({ achievements: [newAchievement, ...state.achievements] }))
      } catch (error) {
        set({ error: 'Failed to add achievement' })
        console.error('Failed to add achievement:', error)
        throw error
      }
    },

    deleteAchievement: async (id: string) => {
      set({ error: null })
      try {
        await apiClient.deleteAchievement(id)
        set((state) => ({ achievements: state.achievements.filter((a) => a.id !== id) }))
      } catch (error) {
        set({ error: 'Failed to delete achievement' })
        console.error('Failed to delete achievement:', error)
        throw error
      }
    },
  })
)
